import React from "react";
import { Card, Table, Badge, Spinner, Alert } from "react-bootstrap";
import "../../../assets/styles/informationLayerHistory.scss";
import { getInformationHistoryByInformationId } from "../../../services/informationHistoryApi";

const statusToBadgeVariant = {
  Executed: "success",
  Accepted: "success",
  Approved: "info",
  Rejected: "danger",
  Pending: "warning",
  Queued: "warning",
  Failed: "danger",
  Withdrawn: "secondary",
};

const statusLabels = {
  Approved: "Zaplanowane",
  Pending: "Do akceptacji",
  Rejected: "Odrzucone",
  Withdrawn: "Wycofane",
  Executed: "Wykonane",
  Queued: "W trakcie wykonywania",
  Failed: "Wystąpił błąd",
};

const formatDateTime = (value, withSeconds = false) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const datePart = date.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
    second: withSeconds ? "2-digit" : undefined,
    hour12: false,
  });

  return `${timePart} ${datePart}`;
};

const normalizeHistoryResponse = (response) => {
  const list = response?.data?.data?.data;
  return Array.isArray(list) ? list : [];
};

const isPlainObject = (value) => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

const normalizeChangesObject = (rawChanges) => {
  if (isPlainObject(rawChanges)) return rawChanges;

  if (Array.isArray(rawChanges)) {
    if (rawChanges.length === 1 && isPlainObject(rawChanges[0])) {
      return rawChanges[0];
    }

    return rawChanges.reduce((acc, entry, index) => {
      acc[`Pozycja ${index + 1}`] = entry;
      return acc;
    }, {});
  }

  if (rawChanges == null || rawChanges === "") return {};

  return { Zmiana: rawChanges };
};

const formatChangeValue = (value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const safeItems = value.map((item) => {
      if (item == null) return "-";
      if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
        return String(item);
      }
      return JSON.stringify(item);
    });
    return safeItems.join(", ");
  }

  if (isPlainObject(value)) {
    return JSON.stringify(value);
  }

  return String(value);
};

const getStatusClassName = (status) => {
  const normalized = String(status || "unknown")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  return `status-${normalized}`;
};

const ChangesHistory = ({ addressData }) => {
  const informationId = addressData?.id;
  const [historyItems, setHistoryItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!informationId) {
      setHistoryItems([]);
      return;
    }

    const controller = new AbortController();

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getInformationHistoryByInformationId(
          informationId,
          controller.signal,
        );
        setHistoryItems(normalizeHistoryResponse(response));
      } catch (err) {
        if (controller.signal.aborted) return;
        setError("Nie udało się pobrać historii zmian.");
        setHistoryItems([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchHistory();

    return () => {
      controller.abort();
    };
  }, [informationId]);

  if (loading) {
    return (
      <div className="w-100 d-flex justify-content-center align-items-center py-4">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!historyItems.length) {
    return <div>Brak historii zmian.</div>;
  }

  return (
    <div className="changes-history">
      {historyItems.map((item) => (
        <div
          key={item.id}
          className={`history-item ${getStatusClassName(item.status)}`}
        >
          <div className="timeline">
            <Badge
              bg={statusToBadgeVariant[item.status] || "secondary"}
              className={`timeline-date ${getStatusClassName(item.status)}`}
            >
              {formatDateTime(item.createdAt)}
            </Badge>
            <div className="timeline-icon">
              <i className="bi bi-arrow-counterclockwise"></i>
            </div>
            <div className="timeline-line" />
          </div>

          <Card className={`history-card ${getStatusClassName(item.status)}`}>
            <Card.Body>
              <Table borderless size="sm" className="mb-4">
                <tbody>
                  <tr>
                    <td className="label">Status:</td>
                    <td className="value fw-bold">
                      <span className={`history-status-pill ${getStatusClassName(item.status)}`}>
                        {statusLabels[item.status] || item.status || "-"}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Dopisał:</td>
                    <td className="value">{item.author || "-"}</td>
                  </tr>
                  <tr>
                    <td className="label">Zaakceptował:</td>
                    <td className="value">{item.approver || "-"}</td>
                  </tr>
                  <tr>
                    <td className="label">Data akceptacji:</td>
                    <td className="value">{formatDateTime(item.acceptedAt, true)}</td>
                  </tr>
                </tbody>
              </Table>

              <Table borderless size="sm">
                <tbody>
                  {Object.entries(normalizeChangesObject(item.changes))
                    .filter(([key]) => key !== "UpdatedDate")
                    .map(([key, value]) => (
                      <tr key={key}>
                        <td className="label">{key}:</td>
                        <td className="value fw-bold">{formatChangeValue(value)}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default ChangesHistory;

